import { memo } from "react";
import PropTypes from "prop-types";
import { FormattedDate, FormattedMessage, FormattedPlural } from "react-intl";
import { OFFER_TYPES } from "app/constants";
import AmountContainer from "app/pages/.shared/AmountContainer";
import "./OffersOverviewItem.scss";
import TransportationPictoContainer from "app/pages/.shared/TransportationPicto/TransportationPictoContainer";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import AvailableDurations from "app/pages/.shared/AvailableDurations";
import MilesEarnedContainer from "app/pages/.shared/MilesEarned/MilesEarnedContainer";
import Popover from "app/components/ui/Popover/Popover";
import { ICON_SIZES } from "app/constants";

const OffersOverviewItem = ({
	offerType,
	label,
	from,
	unit,
	nightCount,
	departureCity,
	allAvailableDurations = [],
	productPriceValue,
	departureDate,
}) => {
	const isPriceAvailable = !isNaN(from);

	const isOfferWithFlight = offerType === OFFER_TYPES.FLIGHT_WITH_ACCOMMODATION;

	// Stop the event(onClick) propagation from the parent element to prevent the Popover content from appearing
	const stopOnClickPropagation = event => {
		event.stopPropagation();
	};
	// eslint-disable-next-line react/no-multi-comp
	const nightCountWording = (...chunks) => (
		<div className="offers-overview-item__duration" onClick={stopOnClickPropagation}>
			{chunks}{" "}
			<Popover
				iconSize={ICON_SIZES.XS}
				iconStyle={{ marginLeft: "4px" }}
				className="offers-overview-item__tooltip-content"
			>
				{isOfferWithFlight ? (
					<>
						<FormattedMessage
							id="product.page.cheapest.fa.offer.info"
							values={{
								duration: nightCount,
								departureCity,
								departureDate: (
									<FormattedDate
										timeZone="UTC"
										day="2-digit"
										month="2-digit"
										year="numeric"
										value={departureDate}
									/>
								),
							}}
						/>
						{allAvailableDurations.length > 1 && Number(productPriceValue) > 0 && (
							<AvailableDurations durations={allAvailableDurations} />
						)}
					</>
				) : (
					<>
						<FormattedMessage
							id="product.page.cheapest.ao.offer.info"
							values={{
								duration: nightCount,
								departureDate: (
									<FormattedDate
										timeZone="UTC"
										day="2-digit"
										month="2-digit"
										year="numeric"
										value={departureDate}
									/>
								),
							}}
						/>
						{allAvailableDurations.length > 1 && Number(productPriceValue) > 0 && (
							<AvailableDurations durations={allAvailableDurations} />
						)}
					</>
				)}
			</Popover>
		</div>
	);
	const nightCountWordingParameters = { count: nightCount };

	return (
		<div className="offers-overview-item" data-testid={`offers-overview-item-${offerType}`}>
			<Typography
				variant={TYPOGRAPHY_VARIANTS.REGULAR}
				isBold
				className="offers-overview-item__label"
			>
				{label}
				{isOfferWithFlight && (
					<span
						className="offers-overview-item-flight-picto"
						data-testid="offers-overview-item-flight-picto"
					>
						<TransportationPictoContainer className="offers-overview-item__transport" />
					</span>
				)}
			</Typography>
			<div>
				<div className="offers-overview-item__amount">
					{isPriceAvailable && (
						<AmountContainer
							amount={from}
							prefix={<FormattedMessage id="offer.per.person.from.label" />}
							suffix={unit}
						/>
					)}
				</div>
				{isPriceAvailable && (
					<FormattedPlural
						data-testid="night-count"
						value={nightCount}
						one={
							<FormattedMessage
								id="general.nights.count.singular"
								values={nightCountWordingParameters}
							>
								{nightCountWording}
							</FormattedMessage>
						}
						other={
							<FormattedMessage
								id="general.nights.count"
								values={nightCountWordingParameters}
							>
								{nightCountWording}
							</FormattedMessage>
						}
					/>
				)}
				<MilesEarnedContainer price={from} />
			</div>
		</div>
	);
};

OffersOverviewItem.propTypes = {
	nightCount: PropTypes.number,
	offerType: PropTypes.string,
	from: PropTypes.number,
	label: PropTypes.string,
	unit: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
	productPriceValue: PropTypes.string,
	allAvailableDurations: PropTypes.arrayOf(PropTypes.number),
	departureCity: PropTypes.string,
	departureDate: PropTypes.number,
};

export default memo(OffersOverviewItem);
